import { useParams, Link, Navigate } from "react-router-dom";
import { useState } from "react";
import { CITIES, SITE } from "@/lib/seoData";
import SeoHead, {
  orgSchema, websiteSchema, breadcrumbSchema, localBusinessSchema, faqSchema
} from "@/components/SeoHead";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import FundingStrip from "@/components/FundingStrip";
import LeadModal from "@/components/LeadModal";
import { Button } from "@/components/ui/button";
import { Phone, MapPin, Building2, Briefcase, Landmark, Flame, Check, ArrowRight } from "lucide-react";

const PRODUCTS = [
  { icon: Building2, title: "Commercial Property", desc: "Office, retail, industrial, mixed-use." },
  { icon: Briefcase, title: "Business Finance", desc: "Working capital, equipment, expansion." },
  { icon: Landmark, title: "Private Credit (Non-Bank)", desc: "Bridging, mezzanine, 2nd-mortgage." },
  { icon: Flame, title: "Private Lender Exit", desc: "Take-out & refinance for short-term loans." },
];

export default function SuburbPage() {
  const { slug } = useParams();
  const city = CITIES.find((c) => c.slug === slug);
  const [open, setOpen] = useState(false);
  const [loanType, setLoanType] = useState("");

  if (!city) return <Navigate to="/" replace />;

  const path = `/locations/${city.slug}`;
  const title = `${city.name} Finance Specialist — Commercial, Business & Private Credit`;
  const description = `${SITE.name} is a ${city.name} finance specialist arranging commercial, business and private credit (non-bank) finance — including bridging take-out and private lender exit. ACL #${SITE.acl}.`;

  const faqs = [
    [`Do you arrange finance in ${city.name}?`, `Yes. ${SITE.name} is an Australian Credit Licence holder (ACL #${SITE.acl}) providing credit assistance to clients in ${city.name} and the wider ${city.region} market. We are a member of AFCA (#${SITE.afca}) and the MFAA (#${SITE.mfaa}).`],
    [`What loans do you arrange in ${city.name}?`, `Commercial property loans, business loans, SMSF property loans, asset and equipment finance, refinance, and private credit / non-bank facilities including bridging take-out finance.`],
    [`How long does finance take to settle in ${city.name}?`, `Timeframes vary by lender, security and complexity. Where timing is important, our team can discuss funding pathways that may be worth considering.`],
    [`Do you charge fees?`, `In most cases we are paid a commission by the lender on settlement — see our Credit Guide for full disclosure of fees and remuneration.`],
  ];

  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Locations", path: "/" },
    { name: city.name, path },
  ];

  const schemas = [
    orgSchema(),
    websiteSchema(),
    localBusinessSchema({ city: city.name, region: city.region, lat: city.lat, lng: city.lng, slug: city.slug }),
    breadcrumbSchema(breadcrumbs),
    faqSchema(faqs),
  ];

  const openWithType = (t) => { setLoanType(t); setOpen(true); };

  return (
    <div className="bg-[#050505] text-white font-outfit min-h-screen">
      <SeoHead title={title} description={description} path={path} schemas={schemas} />
      <SiteHeader />

      {/* Hero */}
      <section className="relative px-6 pt-16 pb-20 sm:pt-24 sm:pb-28 max-w-6xl mx-auto">
        <nav className="text-xs text-zinc-500 mb-6 flex items-center gap-1.5" aria-label="Breadcrumb">
          <Link to="/" className="hover:text-white">Home</Link>
          <span>/</span>
          <span className="text-zinc-300">{city.name}</span>
        </nav>

        <p className="text-xs uppercase tracking-[0.4em] text-[#E50914] mb-4">Finance specialist · {city.region}</p>
        <h1 className="font-bebas text-5xl sm:text-6xl md:text-7xl tracking-tight leading-[0.95] max-w-3xl">
          Finance specialist in {city.name}.
        </h1>
        <p className="text-zinc-400 text-lg mt-5 max-w-2xl leading-relaxed">
          {SITE.name} arranges commercial, business, SMSF, asset and private credit (non-bank) finance for operators across {city.copy}. {SITE.name} helps customers explore funding pathways across banks, non-banks and private capital, supported by Human Review.
        </p>

        <div className="flex flex-wrap items-center gap-3 mt-8">
          <Button onClick={() => openWithType("")} className="bg-[#E50914] hover:bg-[#F6121D] rounded-full h-12 px-6 font-semibold">
            Request a Human Review <ArrowRight className="w-4 h-4 ml-1" />
          </Button>
          <a
            href={`tel:${SITE.phone}`}
            className="inline-flex items-center gap-2 h-12 px-5 rounded-full border border-zinc-800 hover:border-zinc-600 text-white"
          >
            <Phone className="w-4 h-4" /> {SITE.phoneDisplay}
          </a>
        </div>
      </section>

      {/* Products grid */}
      <section className="border-t border-zinc-900 px-6 py-20 max-w-6xl mx-auto">
        <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight mb-10">Loans we arrange in {city.name}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {PRODUCTS.map((p) => (
            <button
              key={p.title}
              type="button"
              onClick={() => openWithType(p.title === "Private Credit (Non-Bank)" ? "Private Credit (Non-Bank)" : p.title)}
              className="text-left bg-[#0c0c0c] border border-zinc-800 rounded-2xl p-6 hover:border-[#E50914]/50 transition-colors"
            >
              <p.icon className="w-5 h-5 text-[#E50914] mb-4" />
              <h3 className="font-semibold">{p.title}</h3>
              <p className="text-zinc-500 text-sm mt-1.5">{p.desc}</p>
            </button>
          ))}
        </div>
      </section>

      {/* Why us in this city */}
      <section className="border-t border-zinc-900 px-6 py-20 max-w-6xl mx-auto">
        <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight mb-8">
          Why {city.name} operators choose {SITE.name}
        </h2>
        <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-3 text-zinc-300">
          {[
            `Local market knowledge across ${city.name} and ${city.region}`,
            "Funding pathways across banks, non-banks and private capital",
            "Human review of your requirements and objectives before options are discussed",
            "Specialist private-lender exit & bridging take-out desk",
            "Australian Credit Licence #" + SITE.acl,
            "AFCA dispute-resolution membership #" + SITE.afca,
            "MFAA professional standards #" + SITE.mfaa,
          ].map((b) => (
            <li key={b} className="flex items-start gap-3">
              <Check className="w-4 h-4 text-[#E50914] mt-1 shrink-0" />
              <span>{b}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* Map */}
      <section className="border-t border-zinc-900 px-6 py-20 max-w-6xl mx-auto">
        <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight mb-8">Servicing {city.name}</h2>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 bg-[#0c0c0c] border border-zinc-800 rounded-2xl p-6 space-y-4">
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500">Office</p>
              <p className="text-white mt-1 leading-relaxed">{SITE.address.street}<br />{SITE.address.locality} {SITE.address.region} {SITE.address.postcode}</p>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500">Phone</p>
              <a href={`tel:${SITE.phone}`} className="text-white hover:text-[#E50914]">{SITE.phoneDisplay}</a>
            </div>
            <div>
              <p className="text-xs uppercase tracking-widest text-zinc-500">Email</p>
              <a href={`mailto:${SITE.email}`} className="text-white hover:text-[#E50914]">{SITE.email}</a>
            </div>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${city.lat},${city.lng}`}
              target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#E50914] text-sm font-medium"
            >
              <MapPin className="w-4 h-4" /> View {city.name} office on Google Maps
            </a>
          </div>
          <div className="lg:col-span-2 rounded-2xl overflow-hidden border border-zinc-800 min-h-[320px]">
            <iframe
              title={`${city.name} map`}
              loading="lazy"
              src={`https://www.google.com/maps?q=${city.lat},${city.lng}&z=12&output=embed`}
              className="w-full h-full min-h-[320px]"
              style={{ border: 0, filter: "invert(0.9) hue-rotate(180deg)" }}
            />
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-zinc-900 px-6 py-20 max-w-3xl mx-auto">
        <h2 className="font-bebas text-3xl sm:text-4xl tracking-tight mb-8">FAQ — {city.name}</h2>
        <div className="space-y-4">
          {faqs.map(([q, a]) => (
            <details key={q} className="group bg-[#0c0c0c] border border-zinc-800 rounded-xl p-5 open:border-[#E50914]/40">
              <summary className="cursor-pointer list-none font-medium flex justify-between items-center">
                {q}
                <span className="text-[#E50914] group-open:rotate-45 transition-transform">+</span>
              </summary>
              <p className="text-zinc-400 text-sm mt-3 leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </section>

      {/* CTA band */}
      <section className="border-t border-zinc-900 px-6 py-20 text-center">
        <h2 className="font-bebas text-4xl sm:text-5xl tracking-tight">Ready to fund your next move in {city.name}?</h2>
        <Button onClick={() => openWithType("")} className="mt-7 bg-[#E50914] hover:bg-[#F6121D] rounded-full h-14 px-8 font-semibold">
          Request a callback
        </Button>
      </section>

      <FundingStrip />
      <SiteFooter />
      <LeadModal open={open} onOpenChange={setOpen} initialLoanType={loanType} sourceChannel="location_page" />
    </div>
  );
}
